import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { AlertTriangle, Maximize } from "lucide-react";

import { STAGES, groupRounds, stageLabel, stageOf } from "../components/tournament/BracketTab";
import { BrandLogo, ChampionCard, GOLD } from "../components/tournament/Brand";
import { CREAM, GREEN, INK } from "../components/tournament/PublicParts";
import { fmtSlot, roundLabel } from "../components/tournament/shared";
import { usePublicTournament } from "../lib/usePublicTournament";

const MAX_COL = 8;
const MAX_ROUNDS = 3;

/** TV mode: auto-rotating brackets / by-location / standings, announcement banner, large type. */
export default function BigScreen() {
  const { slug } = useParams();
  const { data, error, updatedAt } = usePublicTournament(slug);
  const slides = useMemo(() => (data ? buildSlides(data) : []), [data]);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const rotate = (data?.screen?.rotate_sec || 20) * 1000;
  const idx = slides.length ? i % slides.length : 0;
  useWakeLock();
  const takeover = useUrgentTakeover(data?.announcements || []);

  useEffect(() => {
    if (paused || slides.length < 2) return undefined;
    const tm = setTimeout(() => setI((x) => x + 1), rotate);
    return () => clearTimeout(tm);
  }, [i, paused, slides.length, rotate]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setI((x) => x + 1);
      else if (e.key === "ArrowLeft") setI((x) => (x - 1 + slides.length) % Math.max(1, slides.length));
      else if (e.key === " " || e.key === "Enter") setPaused((p) => !p);
      else if (e.key === "f") goFullscreen();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides.length]);

  if (error && !data) return <div style={{ ...root, padding: "4vw", fontSize: "2vw" }} data-testid="screen-error">{error}</div>;
  if (!data) return <div style={{ ...root, padding: "4vw", fontSize: "2vw" }}>Loading…</div>;
  if (data.tournament.tier !== "hub") {
    return <div style={{ ...root, padding: "4vw", fontSize: "2.2vw" }} data-testid="screen-gated">Big Screen is part of The Tournament Hub.</div>;
  }
  const slide = slides[idx];
  const teamsById = Object.fromEntries(data.teams.map((x) => [x.id, x]));
  return (
    <div style={root} data-testid="big-screen">
      <style>{CSS}</style>
      <TopBar data={data} updatedAt={updatedAt} slide={slide} />
      <Ticker items={(data.announcements || []).filter((a) => a.level !== "urgent")} />
      {(data.announcements || []).filter((a) => a.level === "urgent").slice(0, 1).map((a) => (
        <div key={a.id} data-testid="screen-urgent-bar" style={{ background: "#C0392B", padding: "0.8vw 2.4vw", fontSize: "1.9vw", fontWeight: 800, display: "flex", gap: "1vw", alignItems: "center" }}>
          <AlertTriangle size={30} aria-hidden="true" /> {a.body}</div>
      ))}
      <div style={{ flex: 1, minHeight: 0, padding: "1.6vw 2.4vw", position: "relative" }}>
        {slide ? <Slide key={`${idx}-${slide.key}`} slide={slide} data={data} teamsById={teamsById} />
               : <div data-testid="screen-empty" style={{ fontSize: "3vw", opacity: 0.7, paddingTop: "8vw", textAlign: "center" }}>Brackets and schedule coming soon.</div>}
      </div>
      <BottomBar data={data} slug={slug} count={slides.length} idx={idx} paused={paused} rotate={rotate} tick={i} />
      {takeover && (
        <div data-testid="screen-urgent-takeover" style={{ position: "fixed", inset: 0, background: "#C0392B", display: "flex", flexDirection: "column",
                                                            justifyContent: "center", alignItems: "center", padding: "6vw", textAlign: "center", zIndex: 20, animation: "bs-in 300ms ease both" }}>
          <AlertTriangle size={120} aria-hidden="true" />
          <div style={{ fontSize: "2vw", letterSpacing: "0.2em", fontWeight: 800, marginTop: "1.5vw" }}>URGENT</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "6vw", lineHeight: 1.05, marginTop: "1vw" }}>{takeover.body}</div>
        </div>
      )}
      <button className="bs-fs" data-testid="screen-fullscreen" onClick={goFullscreen} aria-label="Full screen">
        <Maximize size={22} aria-hidden="true" /> Full screen</button>
    </div>
  );
}

function goFullscreen() {
  const el = document.documentElement;
  (el.requestFullscreen || el.webkitRequestFullscreen || (() => {})).call(el);
}

function useWakeLock() {
  useEffect(() => {
    let lock = null;
    const get = async () => { try { lock = await navigator.wakeLock?.request("screen"); } catch { /* unsupported */ } };
    get();
    const onVis = () => { if (document.visibilityState === "visible") get(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); try { lock?.release(); } catch { /* noop */ } };
  }, []);
}

function useUrgentTakeover(items) {
  const seen = useRef(new Set());
  const [show, setShow] = useState(null);
  const fresh = items.find((a) => a.level === "urgent" && !seen.current.has(a.id));
  useEffect(() => {
    if (!fresh) return undefined;
    seen.current.add(fresh.id);
    setShow(fresh);
    const tm = setTimeout(() => setShow(null), 30000);
    return () => clearTimeout(tm);
  }, [fresh?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return show;
}

export function buildSlides(data) {
  const views = data.screen?.views || ["bracket", "locations", "standings"];
  const out = [];
  for (const v of views) {
    if (v === "locations" && data.courts.length && data.games.some((g) => g.court_id && g.scheduled_at && !g.is_bye)) {
      for (let p = 0; p < Math.ceil(data.courts.length / 6); p += 1) out.push({ key: `loc-${p}`, kind: "locations", page: p, title: "By location" });
    }
    for (const d of data.divisions) {
      const games = data.games.filter((g) => g.division_id === d.id);
      if (v === "standings" && d.standings?.length) out.push({ key: `st-${d.id}`, kind: "standings", division: d, title: `${d.name} · Pool standings` });
      if (v !== "bracket") continue;
      const res = (data.results || []).find((r) => r.division_id === d.id);
      if (res?.decided) out.push({ key: `res-${d.id}`, kind: "results", division: d, result: res, title: `${d.name} · Results` });
      for (const [k] of STAGES) {
        if (k === "pool") continue;
        const sg = games.filter((g) => stageOf(g) === k);
        if (!sg.length) continue;
        for (const pg of bracketPages(sg)) {
          out.push({ key: `br-${d.id}-${k}-${pg.page}`, kind: "bracket", division: d, stage: k, ...pg,
                     title: `${d.name} · ${stageLabel(k, d)}${pg.pages > 1 ? ` (${pg.page + 1}/${pg.pages})` : ""}` });
        }
      }
    }
  }
  return out;
}

/** Window big brackets to ≤3 live rounds and ≤8 games in the first column per page. */
function bracketPages(games) {
  const { rounds, roundKeys } = groupRounds(games);
  const done = (g) => g.is_bye || ["final", "forfeit", "not_needed"].includes(g.status);
  let start = roundKeys.findIndex((r) => rounds[r].some((g) => !done(g)));
  if (start < 0) start = Math.max(0, roundKeys.length - MAX_ROUNDS);
  start = Math.min(start, Math.max(0, roundKeys.length - MAX_ROUNDS));
  const keys = roundKeys.slice(start, start + MAX_ROUNDS);
  const pages = Math.max(1, Math.ceil(rounds[keys[0]].length / MAX_COL));
  return Array.from({ length: pages }, (_, page) => ({
    page, pages, maxRound: roundKeys[roundKeys.length - 1], firstRound: roundKeys[0],
    cols: keys.map((r) => {
      const all = [...rounds[r]].sort((a, b) => a.slot - b.slot);
      return { round: r, games: all.slice(Math.floor((page * all.length) / pages), Math.floor(((page + 1) * all.length) / pages)) };
    }),
  }));
}

function Slide({ slide, data, teamsById }) {
  return (
    <div data-testid={`screen-slide-${slide.kind}`} style={{ height: "100%", animation: "bs-in 500ms ease both" }}>
      {slide.kind === "bracket" && <BracketSlide slide={slide} teamsById={teamsById} tz={data.tournament.timezone} />}
      {slide.kind === "locations" && <LocationsSlide data={data} page={slide.page} teamsById={teamsById} />}
      {slide.kind === "standings" && <StandingsSlide division={slide.division} pools={data.pools} teamsById={teamsById} />}
      {slide.kind === "results" && <FitBox><div style={{ width: "70vw" }}><ChampionCard result={slide.result} scale={2} testId="screen-results-card" /></div></FitBox>}
    </div>
  );
}

function FitBox({ children }) {
  const outer = useRef(null);
  const inner = useRef(null);
  const [s, setS] = useState(1);
  useLayoutEffect(() => {
    const o = outer.current;
    const n = inner.current;
    if (!o || !n) return;
    setS(Math.min(1, o.clientWidth / n.scrollWidth, o.clientHeight / n.scrollHeight));
  }, [children]);
  return (
    <div ref={outer} style={{ height: "100%", width: "100%", overflow: "hidden" }}>
      <div ref={inner} style={{ transform: `scale(${s})`, transformOrigin: "top left", display: "inline-block", minWidth: s === 1 ? "100%" : 0 }}>{children}</div>
    </div>
  );
}

function BracketSlide({ slide, teamsById, tz }) {
  const label = (r) => (slide.stage === "final" ? (r === 2 ? "If necessary" : "Grand final")
    : slide.stage === "losers" ? `Losers R${r}` : slide.stage === "third_place" ? "3rd place" : roundLabel(r, slide.maxRound));
  return (
    <FitBox>
      <div style={{ display: "flex", gap: "2.4vw", minHeight: "62vh" }}>
        {slide.cols.map((c) => (
          <div key={c.round} style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: "24vw" }}>
            <div style={{ fontSize: "1.3vw", letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.6, marginBottom: "0.8vw" }}>{label(c.round)}</div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-around" }}>
              {c.games.map((g) => <ScreenGame key={g.id} g={g} teamsById={teamsById} tz={tz} />)}
            </div>
          </div>
        ))}
      </div>
    </FitBox>
  );
}

function ScreenGame({ g, teamsById, tz }) {
  const name = (id) => (id ? teamsById[id]?.name || "TBD" : g.is_bye ? "Bye" : "TBD");
  const live = g.status === "scheduled" && g.home_team_id && g.away_team_id;
  return (
    <div data-testid={`screen-game-${g.id}`} style={{ background: CREAM, color: INK, borderRadius: "0.6vw", padding: "0.7vw 1vw", margin: "0.5vw 0",
                                                      borderLeft: `0.5vw solid ${g.winner_team_id ? GREEN : live ? GOLD : "transparent"}` }}>
      {[[g.home_team_id, g.home_score], [g.away_team_id, g.away_score]].map(([tid, sc], k) => {
        const win = g.winner_team_id && g.winner_team_id === tid;
        return (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: "1vw", fontSize: "1.75vw", lineHeight: 1.25,
                                fontWeight: win ? 800 : 500, color: win ? GREEN : INK, opacity: g.winner_team_id && !win ? 0.6 : 1 }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name(tid)}</span><span>{sc ?? ""}</span>
          </div>
        );
      })}
      {live && g.scheduled_at && <div style={{ fontSize: "1.05vw", opacity: 0.65, marginTop: "0.2vw" }}>{fmtSlot(g.scheduled_at, tz, false)}</div>}
    </div>
  );
}

function LocationsSlide({ data, page, teamsById }) {
  const tz = data.tournament.timezone;
  const now = Date.now();
  const courts = data.courts.slice(page * 6, page * 6 + 6);
  const name = (id) => (id ? teamsById[id]?.name || "TBD" : "TBD");
  const games = data.games.filter((g) => !g.is_bye && g.court_id && g.scheduled_at).sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(3, courts.length)}, 1fr)`, gap: "1.4vw", height: "100%" }}>
      {courts.map((c) => {
        const mine = games.filter((g) => g.court_id === c.id);
        const open = mine.filter((g) => g.status === "scheduled");
        const nowG = open.find((g) => new Date(g.scheduled_at).getTime() <= now);
        const next = open.filter((g) => g !== nowG).slice(0, 1)[0];
        const last = mine.filter((g) => g.status !== "scheduled").slice(-1)[0];
        const row = (tag, g, hot) => g && (
          <div style={{ background: hot ? GREEN : "rgba(245,240,232,0.07)", borderRadius: "0.6vw", padding: "0.8vw 1vw", marginTop: "0.8vw" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.1vw", fontWeight: 800, letterSpacing: "0.12em" }}>
              <span>{tag}</span><span>{fmtSlot(g.scheduled_at, tz, false)}</span></div>
            <div style={{ fontSize: "1.75vw", fontWeight: 700, lineHeight: 1.2 }}>{name(g.home_team_id)}</div>
            <div style={{ fontSize: "1.75vw", fontWeight: 700, lineHeight: 1.2 }}>{name(g.away_team_id)}</div>
            {g.status !== "scheduled" && <div style={{ fontSize: "1.5vw", color: GOLD, fontWeight: 800 }}>Final {g.home_score ?? "-"}–{g.away_score ?? "-"}</div>}
          </div>
        );
        return (
          <div key={c.id} data-testid={`screen-location-${c.id}`} style={{ background: "rgba(245,240,232,0.04)", border: "0.5px solid rgba(245,240,232,0.15)", borderRadius: "1vw", padding: "1.2vw" }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "2.8vw", lineHeight: 1 }}>{c.name}</div>
            {row("NOW", nowG, true)}
            {row("UP NEXT", next)}
            {!nowG && !next && row("FINAL", last)}
            {!mine.length && <div style={{ fontSize: "1.6vw", opacity: 0.6, marginTop: "1vw" }}>No games here.</div>}
          </div>
        );
      })}
    </div>
  );
}

function StandingsSlide({ division, pools, teamsById }) {
  const poolName = Object.fromEntries((pools || []).map((p) => [p.id, p.name]));
  return (
    <FitBox>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "2vw" }}>
        {division.standings.map((p) => (
          <div key={p.pool_id} data-testid={`screen-pool-${p.pool_id}`} style={{ minWidth: "28vw", flex: 1 }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "2.6vw" }}>{poolName[p.pool_id] || p.name}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "1.7vw" }}>
              <thead><tr style={{ opacity: 0.6, fontSize: "1.1vw", letterSpacing: "0.1em" }}>
                <th style={{ textAlign: "left" }}>#</th><th style={{ textAlign: "left" }}>TEAM</th><th>W</th><th>L</th><th>T</th><th>PTS</th></tr></thead>
              <tbody>
                {p.rows.map((r) => (
                  <tr key={r.team_id} style={{ borderTop: "0.5px solid rgba(245,240,232,0.2)" }}>
                    <td>{r.place}</td><td style={{ fontWeight: 700, padding: "0.4vw 0" }}>{teamsById[r.team_id]?.name || "TBD"}</td>
                    {[r.w, r.l, r.t, r.pts].map((v, k) => <td key={k} style={{ textAlign: "center" }}>{v}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </FitBox>
  );
}

function TopBar({ data, updatedAt, slide }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const tm = setInterval(() => setNow(new Date()), 15000); return () => clearInterval(tm); }, []);
  const tz = data.tournament.timezone;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "1.6vw", padding: "1.4vw 2.4vw 1vw" }}>
      <BrandLogo branding={data.branding} size={80} testId="screen-logo" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div data-testid="screen-title" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "3.6vw", lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{data.tournament.name}</div>
        <div data-testid="screen-slide-title" style={{ fontSize: "1.6vw", color: "#4db87a", fontWeight: 800, letterSpacing: "0.06em" }}>{slide?.title || ""}</div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: "2.4vw", fontWeight: 800 }}>{now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: tz })}</div>
        <div data-testid="screen-updated" style={{ fontSize: "1vw", opacity: 0.6 }}>
          <span className="bs-dot" /> LIVE · updated {updatedAt ? updatedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit", timeZone: tz }) : "—"}</div>
      </div>
    </div>
  );
}

function Ticker({ items }) {
  if (!items.length) return null;
  const text = items.map((a) => a.body).join("   •   ");
  return (
    <div data-testid="screen-ticker" style={{ background: "rgba(245,240,232,0.08)", overflow: "hidden", whiteSpace: "nowrap", fontSize: "1.7vw", padding: "0.6vw 0" }}>
      <div className="bs-marquee" style={{ animationDuration: `${Math.max(18, text.length / 5)}s` }}>{text}   •   {text}</div>
    </div>
  );
}

function BottomBar({ data, slug, count, idx, paused, rotate, tick }) {
  const sponsors = data.branding?.sponsors || [];
  const url = `${window.location.origin}/t/${slug}`;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "1.6vw", padding: "0.8vw 2.4vw 1.2vw", borderTop: "0.5px solid rgba(245,240,232,0.12)" }}>
      <div data-testid="screen-sponsors" style={{ flex: 1, display: "flex", gap: "1vw", alignItems: "center", overflow: "hidden" }}>
        {sponsors.map((s) => (
          <div key={s.id} style={{ height: "4.2vw", width: "9vw", background: CREAM, borderRadius: "0.5vw", display: "grid", placeItems: "center", padding: "0.3vw", flexShrink: 0 }}>
            <img src={`${process.env.REACT_APP_BACKEND_URL}${s.logo_url}`} alt={s.name} style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} />
          </div>
        ))}
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginBottom: "0.4vw" }}>
          {Array.from({ length: Math.min(count, 24) }, (_, k) => (
            <span key={k} style={{ width: 10, height: 10, borderRadius: 5, background: k === idx ? CREAM : "rgba(245,240,232,0.25)" }} />
          ))}
        </div>
        <div style={{ fontSize: "1.1vw", opacity: 0.7 }}>{paused ? "PAUSED · " : ""}Follow live: <b>{url.replace(/^https?:\/\//, "")}</b></div>
        <div style={{ fontSize: "0.9vw", opacity: 0.5, letterSpacing: "0.14em" }}>POWERED BY CHALKOS</div>
        {!paused && count > 1 && <div key={tick} className="bs-progress" style={{ animationDuration: `${rotate}ms` }} />}
      </div>
      <div data-testid="screen-qr" style={{ background: CREAM, padding: "0.5vw", borderRadius: "0.5vw", lineHeight: 0 }}>
        <QRCodeSVG value={url} size={110} level="M" />
      </div>
    </div>
  );
}

const root = { background: INK, color: CREAM, height: "100vh", width: "100vw", overflow: "hidden", display: "flex", flexDirection: "column",
               fontFamily: "Barlow, system-ui, sans-serif", cursor: "none" };
const CSS = `
@keyframes bs-in { from { opacity: 0; transform: translateY(1vw); } to { opacity: 1; transform: none; } }
@keyframes bs-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@keyframes bs-bar { from { width: 0; } to { width: 100%; } }
@keyframes bs-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
.bs-marquee { display: inline-block; padding-left: 2.4vw; animation: bs-scroll linear infinite; }
.bs-progress { height: 3px; background: #4db87a; margin-top: 0.4vw; animation: bs-bar linear both; }
.bs-dot { display: inline-block; width: 0.6vw; height: 0.6vw; border-radius: 50%; background: #4db87a; animation: bs-pulse 1.6s ease infinite; vertical-align: middle; }
.bs-fs { position: fixed; right: 1.4vw; top: 1.4vw; display: inline-flex; gap: 8px; align-items: center; background: rgba(245,240,232,0.12); color: #F5F0E8;
  border: none; border-radius: 999px; padding: 10px 18px; font-weight: 800; cursor: pointer; opacity: 0; transition: opacity 200ms ease; z-index: 30; }
[data-testid="big-screen"]:hover { cursor: default; }
[data-testid="big-screen"]:hover .bs-fs { opacity: 1; }
:fullscreen .bs-fs { display: none; }
`;
